import React, { useCallback, useEffect, useMemo } from "react";
import { Spinner } from "@myhealth-raccoon/components";
import { useAsyncData } from "@myhealth-raccoon/services";
import moment from "moment";

import { patientService } from "src/services/patient";
import { useSelector } from "src/store";

type Props = {
    onConfirm?(): void;
    onError?(message: string): void;
    formId: string | number;
};

function isAutomedPayload(origin: string, data: unknown): data is AutomedConsentEvent {
    return origin === import.meta.env.VITE_AUTOMED_URL
        && !!data && typeof data === 'object'
        && 'status' in data
        && 'iframe' in data;
}

type AutomedConsentEvent = {
    status: boolean,
    iframe: "Automed Consent Form",
}

export function ConsentEmbed({ onConfirm, formId }: Props) {
    const {
        user_fk,
        patient_id,
        clinic_group_crm,
    } = useSelector(state => state.appointment.reservedBooking!);

    const {
        patientDOB,
    } = useSelector(state => state.bookingFlow);

    const siteId = useSelector(state => state.appointment.clinicId);

    const {
        data: basicDetails
    } = useAsyncData({
        method: patientService.GetBasicDetails,
        service: patientService
    }, siteId);

    const queryString = useMemo(() => {
        if (clinic_group_crm && user_fk && moment(patientDOB).isValid() && basicDetails) {
            return new URLSearchParams(
                {
                    clinic_group_crm: clinic_group_crm.toString(),
                    user_fk: user_fk.toString(),
                    form_id: formId.toString(),
                    patient_id: patient_id.toString(),
                    dob: moment(patientDOB).format("DD-MM-YYYY"),
                    first_name: basicDetails.firstname,
                    last_name: basicDetails.surname
                }

            )
        }
    }, [basicDetails, clinic_group_crm, formId, patientDOB, patient_id, user_fk]);

    const messageCallback = useCallback(({ data, origin }: MessageEvent) => {
        console.debug(`postMessage from ${origin}:`, data)
        if (isAutomedPayload(origin, data)) {
            switch (data.status) {
                case true: // Submission successful
                    onConfirm?.();
                    break;
                case false: // Submission in error? maybe?
                    break;
            }
        }
    }, [onConfirm]);

    useEffect(() => {
        console.debug("registering postMessage listener");
        window.addEventListener("message", messageCallback);
        return () => {
            console.debug("unregistering postMessage listener");
            window.removeEventListener("message", messageCallback);
        }
    }, [messageCallback]);

    return queryString ? <iframe
        className='w-full drop-shadow-xl border border-gray-200 rounded-xl'
        height={500}
        name="Myhealth_consent"
        src={`${import.meta.env.VITE_AUTOMED_URL}/myhealth/cors_api/myhealth_consent_form?${queryString}`}
    /> : <Spinner />
}